import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, Loader2, ChevronRight, RefreshCw, Clock, Users, CalendarClock, CheckCircle2, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CertificationBadge } from '@/components/CertificationBadge';
import { EditCalcMeasureDialog } from '@/components/semantic-models/EditCalcMeasureDialog';

const CountsRow = ({ label, counts, testPrefix }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    {['certified', 'in_review', 'draft', 'rejected', 'deprecated'].map(s => (
      <span key={s} className="text-[10px] flex items-center gap-1" data-testid={`${testPrefix}-count-${s}`}>
        <CertificationBadge status={s} size="xs" />
        <span className="text-muted-foreground font-mono">{counts[s] || 0}</span>
      </span>
    ))}
  </div>
);

export default function CertificationQueuePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aiRequests, setAiRequests] = useState([]);
  const [recert, setRecert] = useState({ measures: [], counts: {} });
  const [govEditId, setGovEditId] = useState(null);
  const [govForm, setGovForm] = useState({ business_owner: '', technical_owner: '', recertify_every_days: '' });
  const [impact, setImpact] = useState({});
  const [impactId, setImpactId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedAi, setExpandedAi] = useState(null);

  // Certify-via-Edit-Calculated-Measure dialog state. Clicking Certify on an AI
  // request opens the full measure editor pre-filled; saving creates the
  // certified measure in the request's associated model.
  const [certDlgOpen, setCertDlgOpen] = useState(false);
  const [certReq, setCertReq] = useState(null);
  const [certEntities, setCertEntities] = useState([]);
  const [certMeasures, setCertMeasures] = useState([]);
  const [ecName, setEcName] = useState('');
  const [ecFormula, setEcFormula] = useState('');
  const [ecDesc, setEcDesc] = useState('');
  const [ecEntity, setEcEntity] = useState('');
  const [ecCursor, setEcCursor] = useState(0);
  const [ecMeasure, setEcMeasure] = useState(null);
  const [modelNames, setModelNames] = useState({});
  const modelLabel = (id) => modelNames[id] || 'Unknown model';

  const openCertifyDialog = async (r) => {
    setCertReq(r);
    setEcName(r.measure_name || '');
    setEcFormula(r.formula || '');
    setEcDesc(r.business_definition || '');
    setEcCursor((r.formula || '').length);
    setEcMeasure({ measure_id: r.request_id, name: r.measure_name, formula: r.formula || '', description: r.business_definition || '' });
    setCertEntities([]);
    setCertMeasures([]);
    setEcEntity('');
    try {
      const ents = await api.getSemanticEntities(r.model_id);
      const list = Array.isArray(ents) ? ents : (ents?.entities || []);
      setCertEntities(list);
      const match = list.find(e => (e.name || '').toLowerCase() === (r.source_entity || '').toLowerCase());
      setEcEntity((match || list[0])?.entity_id || '');
    } catch { /* dialog still opens; entity picker just empty */ }
    try {
      const ms = await api.getSemanticMeasures(r.model_id);
      const mlist = Array.isArray(ms) ? ms : (ms?.measures || []);
      // Only calculated measures (they carry a formula) are referenceable as {name}.
      setCertMeasures(mlist.filter(m => m && m.name && m.formula));
    } catch { /* reference list just stays empty */ }
    setCertDlgOpen(true);
  };

  const saveCertifiedMeasure = async (payload) => {
    if (!certReq) return;
    await api.approveCertRequest(certReq.request_id, {
      name: payload.name, formula: payload.formula, business_definition: payload.description,
    });
    toast.success(`'${payload.name}' certified and added to the model`);
    setCertReq(null);
    await load();
  };

  // Phase 3 inc-6: dual approval (four-eyes) + recertification schedule.
  const approveDual = async (r) => {
    setBusy(prev => new Set(prev).add(`dual-${r.request_id}`));
    try {
      const res = await api.approveCertRequest(r.request_id, {});
      toast.success(res?.message || 'Approval recorded');
      await load();
    } catch (err) { toast.error(err.message || 'Action failed'); }
    finally { setBusy(prev => { const s = new Set(prev); s.delete(`dual-${r.request_id}`); return s; }); }
  };
  const toggleDual = (r) => runAction(`flag-${r.request_id}`,
    () => api.flagCertRequestDual(r.request_id, !r.requires_dual_approval),
    'Updated approval policy');
  const recertify = (m) => runAction(`recert-${m.measure_id}`,
    () => api.recertifyMeasure(m.measure_id, {}),
    'Measure recertified — schedule reset');
  const openGov = (m) => {
    setGovEditId(m.measure_id);
    setGovForm({
      business_owner: m.owners?.business_owner || '',
      technical_owner: m.owners?.technical_owner || '',
      recertify_every_days: m.recertify_every_days || '',
    });
  };
  const saveGov = (m) => runAction(`gov-${m.measure_id}`, async () => {
    await api.setMeasureGovernance(m.measure_id, {
      owners: { business_owner: govForm.business_owner, technical_owner: govForm.technical_owner },
      recertify_every_days: govForm.recertify_every_days ? Number(govForm.recertify_every_days) : null,
    });
    setGovEditId(null);
  }, 'Governance updated');
  const toggleImpact = async (m) => {
    if (impactId === m.measure_id) { setImpactId(null); return; }
    setImpactId(m.measure_id);
    if (!impact[m.measure_id]) {
      try {
        const res = await api.getMeasureImpact(m.measure_id);
        setImpact(prev => ({ ...prev, [m.measure_id]: res }));
      } catch (e) { toast.error(e.message || 'Could not load impact'); setImpactId(null); }
    }
  };

  const RECERT_STYLE = {
    overdue: 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-300',
    due_soon: 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300',
    ok: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
    not_scheduled: 'bg-muted/40 border-border/40 text-muted-foreground',
  };
  const RECERT_LABEL = { overdue: 'Overdue', due_soon: 'Due soon', ok: 'On schedule', not_scheduled: 'No schedule' };


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, cr, ms] = await Promise.all([
        api.getCertificationQueue(),
        api.listCertRequests('pending').catch(() => ({ requests: [] })),
        api.getSemanticModels().catch(() => []),
      ]);
      setData(q);
      setAiRequests(cr?.requests || []);
      setModelNames(Object.fromEntries((ms || []).map(m => [m.model_id, m.name])));
      api.getRecertification().then(r => setRecert(r || { measures: [], counts: {} })).catch(() => {});
    } catch (err) {
      toast.error(err.message || 'Failed to load queue');
      setData({ pending_measures: [], pending_models: [], measure_counts: {}, model_counts: {} });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async (key, fn, successMsg) => {
    setBusy(prev => new Set(prev).add(key));
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (err) {
      if (err.message !== 'cancelled') toast.error(err.message || 'Action failed');
    } finally {
      setBusy(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  };

  if (loading) return (
    <div className="p-6 flex items-center gap-2 text-muted-foreground" data-testid="cert-queue-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading queue…
    </div>
  );

  const mc = data.measure_counts || {};
  const modc = data.model_counts || {};

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="certification-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" /> Certification Queue
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Review pending certification requests and manage measure / model trust.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-7" data-testid="cert-queue-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-2">
        <CountsRow label="Measures" counts={mc} testPrefix="measures" />
        <CountsRow label="Models" counts={modc} testPrefix="models" />
      </div>

      {/* AI Explore certification requests (ungoverned calculations) */}
      <section data-testid="cert-queue-ai-requests">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
          AI Explore requests
          <Badge variant="outline" className="text-[10px]">{aiRequests.length}</Badge>
          <span className="text-[10px] text-muted-foreground font-normal">Calculations the AI computed that users asked to be certified</span>
        </h2>
        {aiRequests.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No AI Explore certification requests pending.</p>
        ) : (
          <div className="space-y-2">
            {aiRequests.map(r => (
              <div key={r.request_id} className="rounded-lg border border-sky-500/25 bg-sky-500/5 p-3 space-y-2"
                data-testid={`cert-airow-${r.request_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="font-medium text-sm">{r.measure_name}</span>
                      <Badge variant="outline" className="text-[9px] border-sky-500/40 text-sky-300">AI calculation</Badge>
                      <span className="text-[10px] text-muted-foreground">{modelLabel(r.model_id)}</span>
                      {r.requires_dual_approval && (
                        <Badge variant="outline" className="text-[9px] border-violet-500/40 text-violet-500 gap-1" data-testid={`cert-ai-dual-badge-${r.request_id}`}>
                          <Users className="w-2.5 h-2.5" /> Needs 2 approvals
                        </Badge>
                      )}
                      {r.status === 'awaiting_second_approval' && (
                        <span className="text-[10px] text-amber-500 font-medium" data-testid={`cert-ai-await-${r.request_id}`}>
                          1 of 2 · signed off by {r.first_approver_name || 'a steward'} — needs a different steward
                        </span>
                      )}
                    </div>
                    {r.formula && <p className="text-[11px] text-muted-foreground/90 font-mono">{r.formula}</p>}
                    <p className="text-[11px] text-foreground/80 mt-1">
                      Requested by <b>{r.requested_by_name || r.requested_by || 'a user'}</b>
                      {r.business_definition ? <> · {r.business_definition}</> : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setExpandedAi(expandedAi === r.request_id ? null : r.request_id)}
                      className="h-6 text-[10px] gap-1"
                      data-testid={`cert-ai-view-${r.request_id}`}>
                      <ChevronRight className={`w-2.5 h-2.5 transition-transform ${expandedAi === r.request_id ? 'rotate-90' : ''}`} /> View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleDual(r)}
                      disabled={busy.has(`flag-${r.request_id}`) || r.status === 'awaiting_second_approval'}
                      className={`h-6 text-[10px] gap-1 ${r.requires_dual_approval ? 'border-violet-500/50 text-violet-500' : ''}`}
                      data-testid={`cert-ai-flag-dual-${r.request_id}`}>
                      <Users className="w-3 h-3" /> {r.requires_dual_approval ? 'Dual: on' : 'Require 2'}
                    </Button>
                    {r.requires_dual_approval ? (
                      <Button size="sm" onClick={() => approveDual(r)}
                        disabled={busy.has(`dual-${r.request_id}`)}
                        className="h-6 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`cert-ai-dual-approve-${r.request_id}`}>
                        <ShieldCheck className="w-3 h-3" /> {r.status === 'awaiting_second_approval' ? 'Second approve' : 'Approve'}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => openCertifyDialog(r)}
                        disabled={busy.has(`airq-${r.request_id}`)}
                        className="h-6 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`cert-ai-approve-${r.request_id}`}>
                        <ShieldCheck className="w-3 h-3" /> Certify
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => runAction(
                        `airej-${r.request_id}`,
                        async () => {
                          const reason = window.prompt('Reject this certification request?\nOptional reason:') ?? null;
                          if (reason === null) throw new Error('cancelled');
                          await api.rejectCertRequest(r.request_id, { reason });
                        },
                        'Request rejected')}
                      disabled={busy.has(`airej-${r.request_id}`)}
                      className="h-6 text-[10px] gap-1"
                      data-testid={`cert-ai-reject-${r.request_id}`}>
                      <ShieldAlert className="w-3 h-3" /> Reject
                    </Button>
                  </div>
                </div>
                {expandedAi === r.request_id && (
                  <div className="mt-1 pt-2 border-t border-sky-500/20 grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-[11px]"
                    data-testid={`cert-ai-detail-${r.request_id}`}>
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium break-words">{r.measure_name}</span>
                    <span className="text-muted-foreground">Calculation</span>
                    <span className="font-mono break-words">{r.formula || <span className="italic text-muted-foreground">— not provided —</span>}</span>
                    <span className="text-muted-foreground">Business definition</span>
                    <span className="break-words">{r.business_definition || <span className="italic text-muted-foreground">—</span>}</span>
                    <span className="text-muted-foreground">Requested by</span>
                    <span>{r.requested_by_name || r.requested_by || '—'}</span>
                    <span className="text-muted-foreground">Model</span>
                    <span className="break-words">{modelLabel(r.model_id)}</span>
                    {r.created_at && (<><span className="text-muted-foreground">Requested at</span>
                      <span>{new Date(r.created_at).toLocaleString()}</span></>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pending measures */}
      <section data-testid="cert-queue-measures">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
          Pending measures
          <Badge variant="outline" className="text-[10px]">{data.pending_measures.length}</Badge>
        </h2>
        {data.pending_measures.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No measures awaiting review.</p>
        ) : (
          <div className="space-y-2">
            {data.pending_measures.map(m => (
              <div key={m.measure_id} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-2"
                data-testid={`cert-row-measure-${m.measure_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm">{m.name}</span>
                      <CertificationBadge status="in_review" size="xs" />
                      <span className="text-[10px] text-muted-foreground">{modelLabel(m.model_id)}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground/90">
                      {m.formula
                        ? <span className="font-mono">{m.formula}</span>
                        : m.scope_filters?.length
                        ? <><span className="font-mono">{(m.aggregation || 'sum').toUpperCase()}({m.base_field})</span>
                            <span className="mx-1">where</span>
                            {m.scope_filters.map((f, i) => (
                              <span key={i}>{i > 0 && <span className="text-primary/80"> AND </span>}
                                <span className="font-mono">{f.field} {f.operator}{f.value !== undefined ? ` ${JSON.stringify(f.value)}` : ''}</span>
                              </span>
                            ))}
                          </>
                        : <span className="italic">no formula</span>}
                    </p>
                    <p className="text-[11px] text-foreground/80 mt-1">
                      <b>Business definition:</b> {m.certification?.business_definition || <span className="italic text-muted-foreground">—</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/semantic-models?model=${m.model_id}&measure=${m.measure_id}`)}
                      className="h-6 text-[10px] gap-1" data-testid={`cert-open-model-${m.measure_id}`}>
                      Open <ChevronRight className="w-2.5 h-2.5" />
                    </Button>
                    <Button size="sm" onClick={() => {
                        const reason = window.prompt(`Reason for certifying "${m.name || 'this measure'}" (recorded with this version):`, '');
                        if (reason == null || !reason.trim()) return;
                        runAction(
                          `cert-${m.measure_id}`,
                          () => api.certifyMeasure(m.measure_id, { reason: reason.trim() }),
                          'Measure certified');
                      }}
                      disabled={busy.has(`cert-${m.measure_id}`)}
                      className="h-6 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid={`cert-approve-${m.measure_id}`}>
                      <ShieldCheck className="w-3 h-3" /> Certify
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectingId(m.measure_id)}
                      className="h-6 text-[10px] gap-1"
                      data-testid={`cert-reject-${m.measure_id}`}>
                      <ShieldAlert className="w-3 h-3" /> Reject
                    </Button>
                  </div>
                </div>
                {rejectingId === m.measure_id && (
                  <div className="flex items-center gap-2 pt-2 border-t border-amber-500/20" data-testid={`cert-reject-form-${m.measure_id}`}>
                    <input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      placeholder="Rejection reason (required)"
                      className="flex-1 h-7 px-2 rounded bg-muted/30 border border-border/30 text-[11px]"
                      data-testid={`cert-reject-reason-${m.measure_id}`} />
                    <Button size="sm" onClick={() => runAction(
                        `rej-${m.measure_id}`,
                        async () => {
                          if (!rejectReason.trim()) throw new Error('reason required');
                          await api.rejectMeasureCertification(m.measure_id, { reason: rejectReason.trim() });
                          setRejectingId(null); setRejectReason('');
                        },
                        'Rejected — author notified')}
                      disabled={busy.has(`rej-${m.measure_id}`)}
                      className="h-7 text-[10px]"
                      data-testid={`cert-reject-confirm-${m.measure_id}`}>Submit</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason(''); }}
                      className="h-7 text-[10px]">Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pending models */}
      <section data-testid="cert-queue-models">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
          Pending model bundles
          <Badge variant="outline" className="text-[10px]">{data.pending_models.length}</Badge>
        </h2>
        {data.pending_models.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No model bundles awaiting review.</p>
        ) : (
          <div className="space-y-2">
            {data.pending_models.map(mdl => (
              <div key={mdl.model_id} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-center justify-between gap-3"
                data-testid={`cert-row-model-${mdl.model_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{mdl.name}</span>
                    <CertificationBadge status="in_review" size="xs" />
                  </div>
                  <p className="text-[11px] text-foreground/80 mt-1">
                    <b>Business definition:</b> {mdl.certification?.business_definition || <span className="italic text-muted-foreground">—</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => runAction(
                      `mcert-${mdl.model_id}`,
                      () => api.certifyModel(mdl.model_id, {}),
                      'Model bundle certified')}
                    disabled={busy.has(`mcert-${mdl.model_id}`)}
                    className="h-6 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid={`cert-approve-model-${mdl.model_id}`}>
                    <ShieldCheck className="w-3 h-3" /> Certify bundle
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => runAction(
                      `mrej-${mdl.model_id}`,
                      async () => {
                        const reason = window.prompt('Rejection reason:');
                        if (!reason) throw new Error('cancelled');
                        await api.rejectModelCertification(mdl.model_id, { reason });
                      },
                      'Model rejected')}
                    disabled={busy.has(`mrej-${mdl.model_id}`)}
                    className="h-6 text-[10px] gap-1"
                    data-testid={`cert-reject-model-${mdl.model_id}`}>
                    <ShieldAlert className="w-3 h-3" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recertification schedule (Phase 3 inc-6) */}
      <section data-testid="cert-queue-recert">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-sky-400" /> Recertification schedule
          <Badge variant="outline" className="text-[10px]">{recert.measures?.length || 0}</Badge>
          {(recert.counts?.overdue || 0) > 0 && (
            <span className="text-[10px] text-rose-500 font-medium" data-testid="recert-overdue-count">{recert.counts.overdue} overdue</span>
          )}
        </h2>
        {(!recert.measures || recert.measures.length === 0) ? (
          <p className="text-xs text-muted-foreground italic">No certified measures yet — recertification appears once measures are certified.</p>
        ) : (
          <div className="space-y-2" data-testid="recert-list">
            {recert.measures.map(m => {
              const rs = m.recertification || {};
              return (
                <div key={m.measure_id} className="rounded-lg border border-border/40 bg-card/30 p-3 space-y-2"
                  data-testid={`recert-row-${m.measure_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-medium text-sm">{m.name}</span>
                        <span className="text-[10px] text-muted-foreground">{modelLabel(m.model_id)}</span>
                        {m.version != null && <Badge variant="outline" className="text-[9px]">v{m.version}</Badge>}
                        <Badge variant="outline" className={`text-[9px] gap-1 ${RECERT_STYLE[rs.state] || ''}`}
                          data-testid={`recert-status-${m.measure_id}`}>
                          <Clock className="w-2.5 h-2.5" /> {RECERT_LABEL[rs.state] || rs.state}
                        </Badge>
                        {m.requires_dual_approval && (
                          <Badge variant="outline" className="text-[9px] border-violet-500/40 text-violet-500 gap-1">
                            <Users className="w-2.5 h-2.5" /> Dual approval
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground" data-testid={`recert-owners-${m.measure_id}`}>
                        <Users className="w-3 h-3 inline mr-1" />
                        Business: <b>{m.owners?.business_owner || '—'}</b> · Technical: <b>{m.owners?.technical_owner || '—'}</b>
                        {rs.due && <> · next review {new Date(rs.due).toLocaleDateString()}</>}
                        {rs.every_days ? <> · every {rs.every_days}d</> : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => toggleImpact(m)}
                        className="h-6 text-[10px] gap-1" data-testid={`recert-impact-${m.measure_id}`}>
                        <GitBranch className="w-3 h-3" /> Impact
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => (govEditId === m.measure_id ? setGovEditId(null) : openGov(m))}
                        className="h-6 text-[10px] gap-1" data-testid={`recert-gov-${m.measure_id}`}>
                        Owners &amp; cadence
                      </Button>
                      <Button size="sm" onClick={() => recertify(m)} disabled={busy.has(`recert-${m.measure_id}`)}
                        className="h-6 text-[10px] gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid={`recert-affirm-${m.measure_id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Recertify
                      </Button>
                    </div>
                  </div>
                  {impactId === m.measure_id && (
                    <div className="pt-2 border-t border-border/30 text-[11px]" data-testid={`recert-impact-panel-${m.measure_id}`}>
                      {!impact[m.measure_id] ? (
                        <span className="text-muted-foreground">Loading impact…</span>
                      ) : impact[m.measure_id].total === 0 ? (
                        <span className="text-muted-foreground">No saved answers, dashboards, alerts or insights reference this measure.</span>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-foreground">
                            <b>{impact[m.measure_id].total}</b> saved item{impact[m.measure_id].total === 1 ? '' : 's'} would be affected by a change:
                            {' '}{impact[m.measure_id].counts.pins || 0} pins · {impact[m.measure_id].counts.dashboards || 0} dashboards · {impact[m.measure_id].counts.alerts || 0} alerts · {impact[m.measure_id].counts.insights || 0} insights
                          </p>
                          {['pins', 'dashboards', 'alerts', 'insights'].flatMap(k => (impact[m.measure_id][k] || []).slice(0, 4).map(it => (
                            <span key={`${k}-${it.id}`} className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded bg-muted/40 border border-border/30 text-muted-foreground">
                              {k.slice(0, -1)}: {it.label}
                            </span>
                          )))}
                        </div>
                      )}
                    </div>
                  )}
                  {govEditId === m.measure_id && (
                    <div className="grid sm:grid-cols-3 gap-2 pt-2 border-t border-border/30" data-testid={`recert-gov-form-${m.measure_id}`}>
                      <input type="text" value={govForm.business_owner}
                        onChange={e => setGovForm(p => ({ ...p, business_owner: e.target.value }))}
                        placeholder="Business owner"
                        className="h-7 px-2 rounded bg-muted/30 border border-border/30 text-[11px]"
                        data-testid={`recert-business-owner-${m.measure_id}`} />
                      <input type="text" value={govForm.technical_owner}
                        onChange={e => setGovForm(p => ({ ...p, technical_owner: e.target.value }))}
                        placeholder="Technical owner"
                        className="h-7 px-2 rounded bg-muted/30 border border-border/30 text-[11px]"
                        data-testid={`recert-technical-owner-${m.measure_id}`} />
                      <div className="flex items-center gap-1.5">
                        <input type="number" min={0} value={govForm.recertify_every_days}
                          onChange={e => setGovForm(p => ({ ...p, recertify_every_days: e.target.value }))}
                          placeholder="Review every N days"
                          className="h-7 px-2 rounded bg-muted/30 border border-border/30 text-[11px] flex-1"
                          data-testid={`recert-cadence-${m.measure_id}`} />
                        <Button size="sm" onClick={() => saveGov(m)} disabled={busy.has(`gov-${m.measure_id}`)}
                          className="h-7 text-[10px]" data-testid={`recert-gov-save-${m.measure_id}`}>Save</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <EditCalcMeasureDialog
        open={certDlgOpen}
        onOpenChange={setCertDlgOpen}
        editCalcMeasure={ecMeasure}
        setEditCalcMeasure={setEcMeasure}
        editCalcName={ecName} setEditCalcName={setEcName}
        editCalcFormula={ecFormula} setEditCalcFormula={setEcFormula}
        editCalcDesc={ecDesc} setEditCalcDesc={setEcDesc}
        editCalcEntity={ecEntity} setEditCalcEntity={setEcEntity}
        editCalcCursorPos={ecCursor} setEditCalcCursorPos={setEcCursor}
        entities={certEntities}
        measures={certMeasures}
        setCalcMeasures={() => {}}
        activeModelId={certReq?.model_id}
        onSave={saveCertifiedMeasure}
      />
    </div>
  );
}
